import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { supabase } from '../config/supabase';
import LoadingSpinner from '../components/LoadingSpinner';

const RestaurantAdminEnhanced = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [authorized, setAuthorized] = useState(false);
  const [staffInfo, setStaffInfo] = useState(null);
  const [activeTab, setActiveTab] = useState('bookings');
  const [bookings, setBookings] = useState([]);
  const [orders, setOrders] = useState([]);
  const [menuItems, setMenuItems] = useState([]);
  const [categories, setCategories] = useState([]);
  const [tables, setTables] = useState([]);
  const [stats, setStats] = useState({
    todayBookings: 0,
    todayOrders: 0,
    todayRevenue: 0,
    activeOrders: 0
  });

  // Menu editing states
  const [showMenuModal, setShowMenuModal] = useState(false);
  const [editingItem, setEditingItem] = useState(null);
  const [menuFormData, setMenuFormData] = useState({
    name: '',
    description: '',
    price: '',
    category_id: '',
    preparation_time: 15,
    is_available: true,
    image_url: '',
    dietary_info: '',
    allergens: ''
  });

  // Category editing states
  const [showCategoryModal, setShowCategoryModal] = useState(false);
  const [editingCategory, setEditingCategory] = useState(null);
  const [categoryFormData, setCategoryFormData] = useState({
    name: '',
    description: '',
    display_order: 0,
    is_active: true
  });

  useEffect(() => {
    checkAuthorization();
  }, []);

  useEffect(() => {
    if (authorized) {
      fetchData();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeTab, authorized]);

  const checkAuthorization = async () => {
    try {
      // Get current session
      const { data: { session }, error: sessionError } = await supabase.auth.getSession();
      
      if (sessionError || !session) {
        navigate('/restaurant-admin-login');
        return;
      }

      const { data, error } = await supabase
        .from('restaurant_staff')
        .select('*')
        .eq('user_id', session.user.id)
        .eq('is_active', true)
        .single();

      if (error || !data) {
        alert('Access denied. You are not authorized as restaurant staff.');
        await supabase.auth.signOut();
        navigate('/restaurant-admin-login');
        return;
      }

      setStaffInfo(data);
      setAuthorized(true);
    } catch (error) {
      console.error('Authorization error:', error);
      navigate('/restaurant-admin-login');
    }
  };

  const fetchData = async () => {
    setLoading(true);
    try {
      const today = new Date().toISOString().split('T')[0];
      
      const { data: todayBookingsData } = await supabase
        .from('restaurant_bookings')
        .select('*', { count: 'exact' })
        .eq('booking_date', today);
      
      const { data: todayOrdersData } = await supabase
        .from('restaurant_orders')
        .select('total_amount')
        .gte('created_at', `${today}T00:00:00`)
        .lte('created_at', `${today}T23:59:59`);
      
      const { data: activeOrdersData } = await supabase
        .from('restaurant_orders')
        .select('*', { count: 'exact' })
        .in('status', ['pending', 'confirmed', 'preparing', 'ready']);

      setStats({
        todayBookings: todayBookingsData?.length || 0,
        todayOrders: todayOrdersData?.length || 0,
        todayRevenue: todayOrdersData?.reduce((sum, order) => sum + parseFloat(order.total_amount), 0) || 0,
        activeOrders: activeOrdersData?.length || 0
      });

      switch(activeTab) {
        case 'bookings':
          await fetchBookings();
          break;
        case 'orders':
          await fetchOrders();
          break;
        case 'menu':
          await fetchMenu();
          break;
        case 'tables':
          await fetchTables();
          break;
        default:
          break;
      }
    } catch (error) {
      console.error('Error fetching data:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchBookings = async () => {
    const { data, error } = await supabase
      .from('restaurant_bookings')
      .select('*')
      .order('booking_date', { ascending: true })
      .order('booking_time', { ascending: true });
    
    if (!error) setBookings(data || []);
  };

  const fetchOrders = async () => {
    const { data, error } = await supabase
      .from('restaurant_orders')
      .select(`
        *,
        order_items (
          *,
          menu_item:menu_items (name)
        )
      `)
      .order('created_at', { ascending: false })
      .limit(50);
    
    if (!error) setOrders(data || []);
  };

  const fetchMenu = async () => {
    const { data: categoriesData } = await supabase
      .from('menu_categories')
      .select('*')
      .order('display_order');
    
    const { data: itemsData } = await supabase
      .from('menu_items')
      .select(`
        *,
        category:menu_categories(name)
      `)
      .order('display_order');
    
    setCategories(categoriesData || []);
    setMenuItems(itemsData || []);
  };

  const fetchTables = async () => {
    const { data, error } = await supabase
      .from('restaurant_tables')
      .select('*')
      .order('table_number');
    
    if (!error) setTables(data || []);
  };

  // Menu Item CRUD Operations
  const openMenuModal = (item = null) => {
    if (item) {
      setEditingItem(item);
      setMenuFormData({
        name: item.name || '',
        description: item.description || '',
        price: item.price || '',
        category_id: item.category_id || '',
        preparation_time: item.preparation_time || 15,
        is_available: item.is_available !== undefined ? item.is_available : true,
        image_url: item.image_url || '',
        dietary_info: item.dietary_info?.join(', ') || '',
        allergens: item.allergens?.join(', ') || ''
      });
    } else {
      setEditingItem(null);
      setMenuFormData({
        name: '',
        description: '',
        price: '',
        category_id: categories[0]?.id || '',
        preparation_time: 15,
        is_available: true,
        image_url: '',
        dietary_info: '',
        allergens: ''
      });
    }
    setShowMenuModal(true);
  };

  const saveMenuItem = async () => {
    try {
      const itemData = {
        name: menuFormData.name,
        description: menuFormData.description,
        price: parseFloat(menuFormData.price),
        category_id: menuFormData.category_id,
        preparation_time: parseInt(menuFormData.preparation_time),
        is_available: menuFormData.is_available,
        image_url: menuFormData.image_url || null,
        dietary_info: menuFormData.dietary_info ? menuFormData.dietary_info.split(',').map(s => s.trim()) : [],
        allergens: menuFormData.allergens ? menuFormData.allergens.split(',').map(s => s.trim()) : []
      };

      if (editingItem) {
        // Update existing item
        const { error } = await supabase
          .from('menu_items')
          .update(itemData)
          .eq('id', editingItem.id);
        
        if (error) throw error;
        alert('Menu item updated successfully!');
      } else {
        // Create new item
        const { error } = await supabase
          .from('menu_items')
          .insert([itemData]);
        
        if (error) throw error;
        alert('Menu item created successfully!');
      }

      setShowMenuModal(false);
      fetchMenu();
    } catch (error) {
      console.error('Error saving menu item:', error);
      alert('Failed to save menu item: ' + error.message);
    }
  };

  const deleteMenuItem = async (itemId) => {
    if (!window.confirm('Are you sure you want to delete this menu item?')) return;

    try {
      const { error } = await supabase
        .from('menu_items')
        .delete()
        .eq('id', itemId);
      
      if (error) throw error;
      alert('Menu item deleted successfully!');
      fetchMenu();
    } catch (error) {
      console.error('Error deleting menu item:', error);
      alert('Failed to delete menu item: ' + error.message);
    }
  };

  const toggleMenuItemAvailability = async (itemId, currentStatus) => {
    try {
      const { error } = await supabase
        .from('menu_items')
        .update({ is_available: !currentStatus })
        .eq('id', itemId);
      
      if (!error) fetchMenu();
    } catch (error) {
      console.error('Error updating menu item:', error);
    }
  };

  // Category CRUD Operations
  const openCategoryModal = (category = null) => {
    if (category) {
      setEditingCategory(category);
      setCategoryFormData({
        name: category.name || '',
        description: category.description || '',
        display_order: category.display_order || 0,
        is_active: category.is_active !== undefined ? category.is_active : true
      });
    } else {
      setEditingCategory(null);
      setCategoryFormData({
        name: '',
        description: '',
        display_order: categories.length,
        is_active: true
      });
    }
    setShowCategoryModal(true);
  };

  const saveCategory = async () => {
    try {
      if (editingCategory) {
        const { error } = await supabase
          .from('menu_categories')
          .update(categoryFormData)
          .eq('id', editingCategory.id);
        
        if (error) throw error;
        alert('Category updated successfully!');
      } else {
        const { error } = await supabase
          .from('menu_categories')
          .insert([categoryFormData]);
        
        if (error) throw error;
        alert('Category created successfully!');
      }

      setShowCategoryModal(false);
      fetchMenu();
    } catch (error) {
      console.error('Error saving category:', error);
      alert('Failed to save category: ' + error.message);
    }
  };

  const deleteCategory = async (categoryId) => {
    const itemsInCategory = menuItems.filter(item => item.category_id === categoryId);
    
    if (itemsInCategory.length > 0) {
      alert(`Cannot delete category. It contains ${itemsInCategory.length} menu item(s). Please move or delete those items first.`);
      return;
    }

    if (!window.confirm('Are you sure you want to delete this category?')) return;

    try {
      const { error } = await supabase
        .from('menu_categories')
        .delete()
        .eq('id', categoryId);
      
      if (error) throw error;
      alert('Category deleted successfully!');
      fetchMenu();
    } catch (error) {
      console.error('Error deleting category:', error);
      alert('Failed to delete category: ' + error.message);
    }
  };

  // Other operations
  const updateBookingStatus = async (bookingId, status) => {
    try {
      const { error } = await supabase
        .from('restaurant_bookings')
        .update({ status })
        .eq('id', bookingId);
      
      if (!error) {
        alert(`Booking ${status}`);
        fetchBookings();
      }
    } catch (error) {
      console.error('Error updating booking:', error);
    }
  };

  const updateTableStatus = async (tableId, status) => {
    try {
      const { error } = await supabase
        .from('restaurant_tables')
        .update({ status })
        .eq('id', tableId);
      
      if (!error) fetchTables();
    } catch (error) {
      console.error('Error updating table:', error);
    }
  };

  const handleLogout = async () => {
    if (window.confirm('Are you sure you want to logout?')) {
      await supabase.auth.signOut();
      navigate('/restaurant-admin-login');
    }
  };

  if (!authorized) {
    return (
      <div className="page-container">
        <div className="page-wrapper">
          <LoadingSpinner />
          <p style={{ textAlign: 'center', marginTop: '2rem' }}>Verifying authorization...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="page-container">
      <div className="page-wrapper" style={{ maxWidth: '1400px' }}>
        {/* Header */}
        <div style={{ 
          background: 'white',
          padding: '2rem',
          borderRadius: '12px',
          marginBottom: '2rem',
          boxShadow: '0 2px 12px rgba(0,0,0,0.08)',
          border: '1px solid #f0f0f0'
        }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: '1.5rem' }}>
            <div>
              <h1 style={{ 
                fontSize: '2rem', 
                fontWeight: '700', 
                color: 'var(--color-gray-dark)', 
                margin: 0,
                marginBottom: '0.5rem'
              }}>
                Restaurant Admin Dashboard
              </h1>
              <p style={{ 
                color: '#6b7280', 
                margin: 0,
                fontSize: '0.95rem'
              }}>
                Welcome, <strong>{staffInfo?.full_name}</strong> <span style={{ 
                  color: 'var(--color-accent-gold)',
                  fontWeight: '600',
                  textTransform: 'uppercase',
                  fontSize: '0.85rem'
                }}>({staffInfo?.role})</span>
              </p>
            </div>
            <div style={{ display: 'flex', gap: '0.75rem', flexWrap: 'wrap' }}>
              <button
                onClick={() => navigate('/kitchen-dashboard')}
                className="btn btn-secondary"
                style={{ padding: '0.75rem 1.5rem' }}
              >
                Kitchen Dashboard
              </button>
              <button
                onClick={handleLogout}
                style={{
                  padding: '0.75rem 1.5rem',
                  background: '#ef4444',
                  color: 'white',
                  border: 'none',
                  borderRadius: '8px',
                  fontWeight: '600',
                  cursor: 'pointer',
                  fontSize: '0.95rem',
                  transition: 'background 0.2s'
                }}
                onMouseOver={(e) => e.currentTarget.style.background = '#dc2626'}
                onMouseOut={(e) => e.currentTarget.style.background = '#ef4444'}
              >
                Logout
              </button>
            </div>
          </div>

          {/* Stats Cards */}
          <div style={{ 
            display: 'grid', 
            gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', 
            gap: '1rem',
            marginBottom: '2rem'
          }}>
            <div className="card" style={{ textAlign: 'center', padding: '1.5rem' }}>
              <h3 style={{ color: 'var(--color-accent-gold)', fontSize: '2rem', margin: 0 }}>{stats.todayBookings}</h3>
              <p style={{ margin: '0.5rem 0 0', color: 'var(--color-gray-dark)' }}>Today's Bookings</p>
            </div>
            <div className="card" style={{ textAlign: 'center', padding: '1.5rem' }}>
              <h3 style={{ color: 'var(--color-accent-gold)', fontSize: '2rem', margin: 0 }}>{stats.todayOrders}</h3>
              <p style={{ margin: '0.5rem 0 0', color: 'var(--color-gray-dark)' }}>Today's Orders</p>
            </div>
            <div className="card" style={{ textAlign: 'center', padding: '1.5rem' }}>
              <h3 style={{ color: 'var(--color-accent-gold)', fontSize: '2rem', margin: 0 }}>£{stats.todayRevenue.toFixed(2)}</h3>
              <p style={{ margin: '0.5rem 0 0', color: 'var(--color-gray-dark)' }}>Today's Revenue</p>
            </div>
            <div className="card" style={{ textAlign: 'center', padding: '1.5rem' }}>
              <h3 style={{ color: 'var(--color-accent-gold)', fontSize: '2rem', margin: 0 }}>{stats.activeOrders}</h3>
              <p style={{ margin: '0.5rem 0 0', color: 'var(--color-gray-dark)' }}>Active Orders</p>
            </div>
          </div>

          {/* Tab Navigation */}
          <div style={{ 
            display: 'inline-flex', 
            gap: '0.5rem',
            marginBottom: '2rem',
            background: 'white',
            padding: '0.5rem',
            borderRadius: '8px',
            boxShadow: '0 2px 8px rgba(0,0,0,0.1)'
          }}>
            {['bookings', 'orders', 'menu', 'tables'].map(tab => (
              <button
                key={tab}
                onClick={() => setActiveTab(tab)}
                className={activeTab === tab ? 'btn btn-primary' : 'btn btn-secondary'}
                style={{ 
                  textTransform: 'capitalize',
                  fontSize: '0.9rem',
                  padding: '0.5rem 1rem',
                  minWidth: 'auto',
                  fontWeight: '600',
                  margin: 0
                }}
              >
                {tab}
              </button>
            ))}
          </div>
        </div>

        {loading ? (
          <LoadingSpinner />
        ) : (
          <div style={{ marginTop: '2rem' }}>
            {/* Bookings Tab */}
            {activeTab === 'bookings' && (
              <div className="card" style={{ padding: '2rem' }}>
                <h2 style={{ color: 'var(--color-accent-gold)', marginBottom: '1.5rem', fontSize: '1.75rem', fontWeight: '600' }}>Reservations</h2>
                {bookings.length === 0 ? (
                  <p style={{ opacity: 0.6, textAlign: 'center', padding: '2rem' }}>No bookings found</p>
                ) : (
                  <div style={{ overflowX: 'auto' }}>
                    <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                      <thead>
                        <tr style={{ borderBottom: '1px solid var(--gold)' }}>
                          <th style={{ padding: '1rem', textAlign: 'left', color: 'var(--gold)' }}>Date</th>
                          <th style={{ padding: '1rem', textAlign: 'left', color: 'var(--gold)' }}>Time</th>
                          <th style={{ padding: '1rem', textAlign: 'left', color: 'var(--gold)' }}>Name</th>
                          <th style={{ padding: '1rem', textAlign: 'left', color: 'var(--gold)' }}>Contact</th>
                          <th style={{ padding: '1rem', textAlign: 'left', color: 'var(--gold)' }}>Party Size</th>
                          <th style={{ padding: '1rem', textAlign: 'left', color: 'var(--gold)' }}>Status</th>
                          <th style={{ padding: '1rem', textAlign: 'left', color: 'var(--gold)' }}>Actions</th>
                        </tr>
                      </thead>
                      <tbody>
                        {bookings.map(booking => (
                          <tr key={booking.id} style={{ borderBottom: '1px solid rgba(255, 255, 255, 0.1)' }}>
                            <td style={{ padding: '1rem' }}>{new Date(booking.booking_date).toLocaleDateString()}</td>
                            <td style={{ padding: '1rem' }}>{booking.booking_time}</td>
                            <td style={{ padding: '1rem' }}>{booking.customer_name}</td>
                            <td style={{ padding: '1rem', fontSize: '0.85rem' }}>
                              {booking.customer_email}<br/>
                              {booking.customer_phone}
                            </td>
                            <td style={{ padding: '1rem' }}>{booking.party_size}</td>
                            <td style={{ padding: '1rem' }}>
                              <span style={{
                                padding: '0.25rem 0.75rem',
                                background: booking.status === 'confirmed' ? '#4CAF50' : 
                                           booking.status === 'cancelled' ? '#f44336' : '#ff9800',
                                color: 'white',
                                borderRadius: '4px',
                                fontSize: '0.85rem'
                              }}>
                                {booking.status}
                              </span>
                            </td>
                            <td style={{ padding: '1rem' }}>
                              {booking.status === 'pending' && (
                                <div style={{ display: 'flex', gap: '0.5rem' }}>
                                  <button
                                    onClick={() => updateBookingStatus(booking.id, 'confirmed')}
                                    style={{
                                      padding: '0.25rem 0.75rem',
                                      background: '#4CAF50',
                                      color: 'white',
                                      border: 'none',
                                      borderRadius: '4px',
                                      cursor: 'pointer',
                                      fontSize: '0.85rem'
                                    }}
                                  >
                                    Confirm
                                  </button>
                                  <button
                                    onClick={() => updateBookingStatus(booking.id, 'cancelled')}
                                    style={{
                                      padding: '0.25rem 0.75rem',
                                      background: '#f44336',
                                      color: 'white',
                                      border: 'none',
                                      borderRadius: '4px',
                                      cursor: 'pointer',
                                      fontSize: '0.85rem'
                                    }}
                                  >
                                    Cancel
                                  </button>
                                </div>
                              )}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>
            )}

            {/* Orders Tab */}
            {activeTab === 'orders' && (
              <div className="card" style={{ padding: '2rem' }}>
                <h2 style={{ color: 'var(--color-accent-gold)', marginBottom: '1.5rem', fontSize: '1.75rem', fontWeight: '600' }}>Order History</h2>
                {orders.length === 0 ? (
                  <p style={{ opacity: 0.6, textAlign: 'center', padding: '2rem' }}>No orders found</p>
                ) : (
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
                    {orders.map(order => (
                      <div key={order.id} style={{ 
                        padding: '1rem', 
                        background: 'rgba(255, 255, 255, 0.05)',
                        borderRadius: '8px',
                        border: '1px solid rgba(255, 255, 255, 0.1)'
                      }}>
                        <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '0.5rem' }}>
                          <div>
                            <strong>Order #{order.order_number}</strong>
                            <span style={{ marginLeft: '1rem', opacity: 0.7 }}>
                              Table {order.table_number}
                            </span>
                          </div>
                          <div>
                            <span style={{
                              padding: '0.25rem 0.75rem',
                              background: order.status === 'completed' ? '#4CAF50' : 
                                         order.status === 'cancelled' ? '#f44336' : '#ff9800',
                              color: 'white',
                              borderRadius: '4px',
                              fontSize: '0.85rem'
                            }}>
                              {order.status}
                            </span>
                          </div>
                        </div>
                        
                        <div style={{ fontSize: '0.85rem', opacity: 0.7, marginBottom: '0.5rem' }}>
                          {new Date(order.created_at).toLocaleString()}
                        </div>

                        <div style={{ marginBottom: '0.5rem' }}>
                          {order.order_items.map(item => (
                            <div key={item.id} style={{ fontSize: '0.9rem', padding: '0.25rem 0' }}>
                              {item.quantity}x {item.menu_item?.name} - £{(item.price * item.quantity).toFixed(2)}
                            </div>
                          ))}
                        </div>

                        <div style={{ 
                          display: 'flex', 
                          justifyContent: 'space-between',
                          paddingTop: '0.5rem',
                          borderTop: '1px solid rgba(255, 255, 255, 0.1)'
                        }}>
                          <strong>Total:</strong>
                          <strong style={{ color: 'var(--gold)' }}>£{order.total_amount}</strong>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}

            {/* Menu Tab - ENHANCED */}
            {activeTab === 'menu' && (
              <div>
                <div className="card" style={{ marginBottom: '1.5rem', padding: '2rem' }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: '1rem' }}>
                    <h2 style={{ color: 'var(--color-accent-gold)', margin: 0, fontSize: '1.75rem', fontWeight: '600' }}>Menu Management</h2>
                    <div style={{ display: 'flex', gap: '1rem', flexWrap: 'wrap' }}>
                      <button
                        onClick={() => openCategoryModal()}
                        className="btn btn-secondary"
                        style={{ padding: '0.75rem 1.5rem' }}
                      >
                        + Add Category
                      </button>
                      <button
                        onClick={() => openMenuModal()}
                        className="btn btn-primary"
                        style={{ padding: '0.75rem 1.5rem' }}
                      >
                        + Add Menu Item
                      </button>
                    </div>
                  </div>
                </div>

                {/* Categories Management */}
                <div className="card" style={{ marginBottom: '2rem', padding: '2rem' }}>
                  <h3 style={{ color: 'var(--gold)', marginBottom: '1rem' }}>Categories</h3>
                  <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(250px, 1fr))', gap: '1rem' }}>
                    {categories.map(category => (
                      <div key={category.id} style={{ 
                        padding: '1rem', 
                        background: 'rgba(255, 255, 255, 0.05)',
                        borderRadius: '8px',
                        border: `1px solid ${category.is_active ? 'var(--gold)' : '#f44336'}`
                      }}>
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'start', marginBottom: '0.5rem' }}>
                          <h4 style={{ margin: 0 }}>{category.name}</h4>
                          <span style={{
                            padding: '0.25rem 0.5rem',
                            background: category.is_active ? '#4CAF50' : '#f44336',
                            color: 'white',
                            borderRadius: '4px',
                            fontSize: '0.75rem'
                          }}>
                            {category.is_active ? 'Active' : 'Inactive'}
                          </span>
                        </div>
                        <p style={{ fontSize: '0.85rem', opacity: 0.8, marginBottom: '1rem' }}>
                          {category.description || 'No description'}
                        </p>
                        <p style={{ fontSize: '0.85rem', opacity: 0.6, marginBottom: '1rem' }}>
                          Order: {category.display_order} | Items: {menuItems.filter(item => item.category_id === category.id).length}
                        </p>
                        <div style={{ display: 'flex', gap: '0.5rem' }}>
                          <button
                            onClick={() => openCategoryModal(category)}
                            style={{
                              flex: 1,
                              padding: '0.5rem',
                              background: 'transparent',
                              border: '1px solid var(--gold)',
                              color: 'var(--gold)',
                              borderRadius: '4px',
                              cursor: 'pointer',
                              fontSize: '0.85rem'
                            }}
                          >
                            Edit
                          </button>
                          <button
                            onClick={() => deleteCategory(category.id)}
                            style={{
                              flex: 1,
                              padding: '0.5rem',
                              background: '#f44336',
                              color: 'white',
                              border: 'none',
                              borderRadius: '4px',
                              cursor: 'pointer',
                              fontSize: '0.85rem'
                            }}
                          >
                            Delete
                          </button>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>

                {/* Menu Items by Category */}
                {categories.map(category => (
                  <div key={category.id} className="card" style={{ marginBottom: '2rem', padding: '2rem' }}>
                    <h3 style={{ color: 'var(--color-accent-gold)', marginBottom: '1.5rem', fontSize: '1.4rem', fontWeight: '600' }}>{category.name}</h3>
                    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(320px, 1fr))', gap: '1.5rem' }}>
                      {menuItems
                        .filter(item => item.category_id === category.id)
                        .map(item => (
                          <div key={item.id} style={{ 
                            padding: '1.5rem', 
                            background: 'white',
                            borderRadius: '12px',
                            border: `2px solid ${item.is_available ? 'var(--color-accent-gold)' : '#ef4444'}`,
                            boxShadow: '0 2px 8px rgba(0,0,0,0.08)',
                            transition: 'transform 0.2s, box-shadow 0.2s'
                          }}>
                            {item.image_url && (
                              <img 
                                src={item.image_url} 
                                alt={item.name}
                                style={{ width: '100%', height: '150px', objectFit: 'cover', borderRadius: '4px', marginBottom: '0.5rem' }}
                              />
                            )}
                            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'start', marginBottom: '0.75rem' }}>
                              <h4 style={{ margin: 0, color: 'var(--color-gray-dark)', fontSize: '1.1rem', fontWeight: '600' }}>{item.name}</h4>
                              <span style={{
                                padding: '0.35rem 0.75rem',
                                background: item.is_available ? '#10b981' : '#ef4444',
                                color: 'white',
                                borderRadius: '6px',
                                fontSize: '0.75rem',
                                fontWeight: '600'
                              }}>
                                {item.is_available ? 'Available' : 'Unavailable'}
                              </span>
                            </div>
                            <p style={{ fontSize: '0.9rem', color: '#6b7280', marginBottom: '0.75rem', lineHeight: '1.5' }}>
                              {item.description}
                            </p>
                            <p style={{ fontSize: '1.4rem', fontWeight: 'bold', color: 'var(--color-accent-gold)', marginBottom: '0.75rem' }}>
                              £{parseFloat(item.price).toFixed(2)}
                            </p>
                            {item.preparation_time && (
                              <p style={{ fontSize: '0.85rem', color: '#6b7280', marginBottom: '0.75rem' }}>
                                ⏱ {item.preparation_time} mins
                              </p>
                            )}
                            {item.dietary_info && item.dietary_info.length > 0 && (
                              <p style={{ fontSize: '0.85rem', color: '#6b7280', marginBottom: '1rem' }}>
                                {item.dietary_info.join(', ')}
                              </p>
                            )}
                            <div style={{ display: 'flex', gap: '0.75rem', marginTop: '1rem' }}>
                              <button
                                onClick={() => openMenuModal(item)}
                                className="btn btn-secondary"
                                style={{ flex: 1, padding: '0.65rem' }}
                              >
                                Edit
                              </button>
                              <button
                                onClick={() => deleteMenuItem(item.id)}
                                style={{
                                  flex: 1,
                                  padding: '0.65rem',
                                  background: '#ef4444',
                                  color: 'white',
                                  border: 'none',
                                  borderRadius: '8px',
                                  cursor: 'pointer',
                                  fontSize: '0.9rem',
                                  fontWeight: '600',
                                  transition: 'background 0.2s'
                                }}
                                onMouseOver={(e) => e.currentTarget.style.background = '#dc2626'}
                                onMouseOut={(e) => e.currentTarget.style.background = '#ef4444'}
                              >
                                Delete
                              </button>
                            </div>
                            <button
                              onClick={() => toggleMenuItemAvailability(item.id, item.is_available)}
                              style={{
                                width: '100%',
                                padding: '0.75rem',
                                marginTop: '0.75rem',
                                background: item.is_available ? '#f59e0b' : '#10b981',
                                color: 'white',
                                border: 'none',
                                borderRadius: '8px',
                                cursor: 'pointer',
                                fontSize: '0.9rem',
                                fontWeight: '600'
                              }}
                            >
                              {item.is_available ? '❌ Mark Unavailable' : '✓ Mark Available'}
                            </button>
                          </div>
                        ))}
                    </div>
                    {menuItems.filter(item => item.category_id === category.id).length === 0 && (
                      <p style={{ opacity: 0.6, textAlign: 'center', padding: '2rem' }}>
                        No items in this category. Click "Add Menu Item" to add one.
                      </p>
                    )}
                  </div>
                ))}
              </div>
            )}

            {/* Tables Tab */}
            {activeTab === 'tables' && (
              <div className="card" style={{ padding: '2rem' }}>
                <h2 style={{ color: 'var(--color-accent-gold)', marginBottom: '1.5rem', fontSize: '1.75rem', fontWeight: '600' }}>Table Management</h2>
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(200px, 1fr))', gap: '1rem' }}>
                  {tables.map(table => (
                    <div key={table.id} style={{ 
                      padding: '1.5rem', 
                      background: 'rgba(255, 255, 255, 0.05)',
                      borderRadius: '8px',
                      border: `2px solid ${
                        table.status === 'available' ? '#4CAF50' :
                        table.status === 'occupied' ? '#ff9800' :
                        table.status === 'reserved' ? '#2196F3' :
                        '#f44336'
                      }`,
                      textAlign: 'center'
                    }}>
                      <h3 style={{ color: 'var(--gold)', marginBottom: '0.5rem' }}>
                        Table {table.table_number}
                      </h3>
                      <p style={{ fontSize: '0.85rem', opacity: 0.7, marginBottom: '0.5rem' }}>
                        Code: {table.table_code}
                      </p>
                      <p style={{ fontSize: '0.85rem', opacity: 0.7, marginBottom: '1rem' }}>
                        Capacity: {table.capacity} guests
                      </p>
                      <p style={{ 
                        padding: '0.5rem',
                        background: table.status === 'available' ? '#4CAF50' :
                                   table.status === 'occupied' ? '#ff9800' :
                                   table.status === 'reserved' ? '#2196F3' :
                                   '#f44336',
                        color: 'white',
                        borderRadius: '4px',
                        marginBottom: '1rem',
                        textTransform: 'uppercase',
                        fontSize: '0.85rem',
                        fontWeight: 'bold'
                      }}>
                        {table.status}
                      </p>
                      <select
                        value={table.status}
                        onChange={(e) => updateTableStatus(table.id, e.target.value)}
                        style={{
                          width: '100%',
                          padding: '0.5rem',
                          background: 'rgba(255, 255, 255, 0.05)',
                          border: '1px solid var(--gold)',
                          borderRadius: '4px',
                          color: 'white'
                        }}
                      >
                        <option value="available">Available</option>
                        <option value="occupied">Occupied</option>
                        <option value="reserved">Reserved</option>
                        <option value="maintenance">Maintenance</option>
                      </select>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        )}

        {/* Menu Item Modal */}
        {showMenuModal && (
          <div style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            background: 'rgba(0, 0, 0, 0.5)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 1000,
            padding: '2rem',
            overflowY: 'auto'
          }}>
            <div className="card" style={{
              maxWidth: '600px',
              borderRadius: '8px',
              padding: '2rem',
              maxWidth: '600px',
              width: '100%',
              maxHeight: '90vh',
              overflowY: 'auto'
            }}>
              <h2 style={{ color: 'var(--color-accent-gold)', marginBottom: '1.5rem' }}>
                {editingItem ? 'Edit Menu Item' : 'Add Menu Item'}
              </h2>

              <div className="form-group">
                <label className="form-label">
                  Item Name *
                </label>
                <input
                  type="text"
                  className="form-input"
                  value={menuFormData.name}
                  onChange={(e) => setMenuFormData({...menuFormData, name: e.target.value})}
                  required
                />
              </div>

              <div className="form-group">
                <label className="form-label">
                  Description
                </label>
                <textarea
                  className="form-input"
                  value={menuFormData.description}
                  onChange={(e) => setMenuFormData({...menuFormData, description: e.target.value})}
                  rows="3"
                  style={{ resize: 'vertical' }}
                />
              </div>

              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem', marginBottom: '1rem' }}>
                <div className="form-group">
                  <label className="form-label">
                    Price (£) *
                  </label>
                  <input
                    type="number"
                    step="0.01"
                    className="form-input"
                    value={menuFormData.price}
                    onChange={(e) => setMenuFormData({...menuFormData, price: e.target.value})}
                    required
                  />
                </div>

                <div className="form-group">
                  <label className="form-label">
                    Prep Time (mins)
                  </label>
                  <input
                    type="number"
                    className="form-input"
                    value={menuFormData.preparation_time}
                    onChange={(e) => setMenuFormData({...menuFormData, preparation_time: e.target.value})}
                  />
                </div>
              </div>

              <div className="form-group">
                <label className="form-label">
                  Category *
                </label>
                <select
                  className="form-input"
                  value={menuFormData.category_id}
                  onChange={(e) => setMenuFormData({...menuFormData, category_id: e.target.value})}
                  required
                >
                  <option value="">Select a category</option>
                  {categories.map(cat => (
                    <option key={cat.id} value={cat.id}>{cat.name}</option>
                  ))}
                </select>
              </div>

              <div className="form-group">
                <label className="form-label">
                  Image URL
                </label>
                <input
                  type="text"
                  className="form-input"
                  value={menuFormData.image_url}
                  onChange={(e) => setMenuFormData({...menuFormData, image_url: e.target.value})}
                  placeholder="https://example.com/image.jpg"
                />
              </div>

              <div className="form-group">
                <label className="form-label">
                  Dietary Info (comma-separated)
                </label>
                <input
                  type="text"
                  className="form-input"
                  value={menuFormData.dietary_info}
                  onChange={(e) => setMenuFormData({...menuFormData, dietary_info: e.target.value})}
                  placeholder="Vegan, Gluten-free, etc."
                />
              </div>

              <div className="form-group">
                <label className="form-label">
                  Allergens (comma-separated)
                </label>
                <input
                  type="text"
                  className="form-input"
                  value={menuFormData.allergens}
                  onChange={(e) => setMenuFormData({...menuFormData, allergens: e.target.value})}
                  placeholder="Nuts, Dairy, Eggs, etc."
                />
              </div>

              <div className="form-group">
                <label style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', cursor: 'pointer' }}>
                  <input
                    type="checkbox"
                    checked={menuFormData.is_available}
                    onChange={(e) => setMenuFormData({...menuFormData, is_available: e.target.checked})}
                    style={{ cursor: 'pointer' }}
                  />
                  <span>Available for ordering</span>
                </label>
              </div>

              <div style={{ display: 'flex', gap: '1rem' }}>
                <button
                  onClick={saveMenuItem}
                  className="btn btn-primary"
                  style={{ flex: 1 }}
                >
                  {editingItem ? 'Update Item' : 'Create Item'}
                </button>
                <button
                  onClick={() => setShowMenuModal(false)}
                  className="btn btn-secondary"
                  style={{ flex: 1 }}
                >
                  Cancel
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Category Modal */}
        {showCategoryModal && (
          <div style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            background: 'rgba(0, 0, 0, 0.5)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 1000
          }}>
            <div className="card" style={{
              maxWidth: '500px',
              width: '100%',
              padding: '2rem'
            }}>
              <h2 style={{ color: 'var(--color-accent-gold)', marginBottom: '1.5rem' }}>
                {editingCategory ? 'Edit Category' : 'Add Category'}
              </h2>

              <div className="form-group">
                <label className="form-label">
                  Category Name *
                </label>
                <input
                  type="text"
                  className="form-input"
                  value={categoryFormData.name}
                  onChange={(e) => setCategoryFormData({...categoryFormData, name: e.target.value})}
                  required
                />
              </div>

              <div className="form-group">
                <label className="form-label">
                  Description
                </label>
                <textarea
                  className="form-input"
                  value={categoryFormData.description}
                  onChange={(e) => setCategoryFormData({...categoryFormData, description: e.target.value})}
                  rows="2"
                  style={{ resize: 'vertical' }}
                />
              </div>

              <div className="form-group">
                <label className="form-label">
                  Display Order
                </label>
                <input
                  type="number"
                  className="form-input"
                  value={categoryFormData.display_order}
                  onChange={(e) => setCategoryFormData({...categoryFormData, display_order: e.target.value})}
                />
              </div>

              <div className="form-group">
                <label style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', cursor: 'pointer' }}>
                  <input
                    type="checkbox"
                    checked={categoryFormData.is_active}
                    onChange={(e) => setCategoryFormData({...categoryFormData, is_active: e.target.checked})}
                    style={{ cursor: 'pointer' }}
                  />
                  <span>Active</span>
                </label>
              </div>

              <div style={{ display: 'flex', gap: '1rem' }}>
                <button
                  onClick={saveCategory}
                  className="btn btn-primary"
                  style={{ flex: 1 }}
                >
                  {editingCategory ? 'Update Category' : 'Create Category'}
                </button>
                <button
                  onClick={() => setShowCategoryModal(false)}
                  className="btn btn-secondary"
                  style={{ flex: 1 }}
                >
                  Cancel
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default RestaurantAdminEnhanced;
